"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
export default function ExpertLungHealthManagement() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    title: "CLINICAL TRIAGE",
    description: "Review of symptoms, medical history, medications, prior testing, and current breathing or sleep-related concerns."
  }, {
    number: "02",
    title: "EVALUATION & TESTING",
    description: "Review of available test results and, when appropriate, pulmonary function testing, imaging, or sleep study information."
  }, {
    number: "03",
    title: "CARE PATHWAY",
    description: "A personalized care plan focused on treatment options, follow-up needs, and practical next steps."
  }];
  return <section data-section-id="3913" id="expert-lung-health-management" className="relative bg-background text-foreground overflow-hidden min-h-[75vh] flex items-center py-16 lg:py-0">
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0 items-center">
        {}
        <div ref={leftRef} className={`lg:col-span-6 px-6 md:px-12 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="flex items-center gap-4 mb-10">
            <span className="h-px w-12 bg-primary" />
            <span className="font-mono text-xs tracking-[0.15em] uppercase font-bold" style={{
            color: "var(--primary)"
          }}>
              Specialized Pulmonary Care
            </span>
            <span className="h-px w-12 bg-primary" />
          </div>

          {}
          <h1 className="font-default font-semibold text-foreground leading-[1.1] mb-8 tracking-tight" style={{
          fontSize: "clamp(2.5rem, 5vw, 3.5rem)"
        }}>
            Expert Pulmonary Care Evaluation
          </h1>

          {}
          <p className="font-default text-base md:text-lg leading-relaxed max-w-[440px] mb-12" style={{
          color: "var(--slate-text)"
        }}>Chaudhry Pulmonary Associates provides adult pulmonary care for breathing concerns, chronic lung conditions, COPD, asthma, chronic cough, shortness of breath, and related respiratory symptoms. Care is led by Dr. Azmat Qayyum with a focus on clear evaluation, treatment planning, and follow-up.</p>

          {}
          <div className="max-w-[480px] mb-12">
            {steps.map((step, i) => <div key={i} data-index={i} className={`grid grid-cols-[auto_1fr] gap-6 py-6 ${i !== steps.length - 1 ? "border-b border-border" : ""} ${i === 0 ? "border-t border-border" : ""}`}>
                <span className="font-serif text-3xl md:text-4xl font-medium text-foreground leading-none pt-1">
                  {step.number}
                </span>
                <div>
                  <h3 className="font-mono text-xs tracking-[0.15em] uppercase text-foreground mb-2 font-bold">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm leading-relaxed" style={{
                color: "var(--slate-text)"
              }}>
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <Link to="tel:+14126729240">
            <Button size="lg" className="font-mono text-xs tracking-[0.15em] uppercase rounded-md h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 transition-transform hover:-translate-y-0.5">
              Call to Schedule
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-6 relative w-full h-full transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <div className="relative w-full aspect-[3/4] lg:aspect-auto lg:h-[75vh] overflow-hidden">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/pulmonary_consultation_medical_office.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/pulmonary_consultation_medical_office.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/pulmonary_consultation_medical_office.png-wvc-sizes" alt="Professional medical consultation room with clean modern equipment and natural light" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" src="https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_52-PM-2.png" />

            {}
            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 bg-background border border-border p-6 md:p-7 max-w-[280px] shadow-md">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-6 bg-primary" />
                <span className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold" style={{
                color: "var(--primary)"
              }}>
                  The Practice
                </span>
              </div>
              <p className="font-mono text-xs tracking-[0.1em] uppercase text-foreground leading-relaxed mb-2">
                ADULT PULMONARY CARE
                <br />
                <span style={{
                color: "var(--slate-text)"
              }}>SLEEP MEDICINE</span>
              </p>
              <div className="h-px w-full bg-border my-3" />
              <p className="font-mono text-xs tracking-[0.1em] uppercase text-foreground">
                Pulmonary Care
                <br />
                <span style={{
                color: "var(--slate-text)"
              }}>& Sleep Medicine</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}